// CB:BEGIN admin-add-homeowner
import React, { useState } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface AdminAddHomeownerProps {
  onNavigate?: (screen: string) => void;
}

const AdminAddHomeowner: React.FC<AdminAddHomeownerProps> = ({ onNavigate }) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Homeowner Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [textOptIn, setTextOptIn] = useState(false);

  // Project Information
  const [projectType, setProjectType] = useState('ROOF');
  const [desiredStart, setDesiredStart] = useState('');
  const [roofScopeOnly, setRoofScopeOnly] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [extraInfo, setExtraInfo] = useState('');

  // Clerk Account Information
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!firstName || !lastName || !email || !address || !password) {
        throw new Error('Please fill in all required fields');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // CB:BEGIN construct-full-address
      // Construct full formatted address from components
      const fullAddress = [address, city, state, zipCode]
        .filter(Boolean)
        .join(', ') || address || 'Address TBD';
      // CB:END construct-full-address
      
      // Create homeowner with project
      const homeownerData = {
        // Homeowner Information
        firstName,
        lastName,
        email,
        phone,
        address: fullAddress, // CB:BEGIN use-full-formatted-address - Use full formatted address
        city,
        state,
        zipCode,
        textOptIn,
        
        // Clerk Account
        password,
        
        // Project Information
        project: {
          address: fullAddress, // CB:BEGIN use-full-formatted-address - Use full formatted address for project
          projectType,
          desiredStart: desiredStart ? new Date(desiredStart).toISOString() : null,
          roofScopeOnly,
          isPartial,
          extraInfo,
          status: 'DRAFT'
        }
      };

      const response = await api.post('/api/admin/homeowners', homeownerData);
      
      if (response.success) {
        setSuccess(true);
        // Reset form
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setAddress('');
        setCity('');
        setState('');
        setZipCode('');
        setTextOptIn(false);
        setProjectType('ROOF');
        setDesiredStart('');
        setRoofScopeOnly(false);
        setIsPartial(false);
        setExtraInfo('');
        setPassword('');
        setConfirmPassword('');
      } else {
        throw new Error(response.error || 'Failed to create homeowner');
      }
    } catch (err) {
      console.error('Error creating homeowner:', err);
      setError(err instanceof Error ? err.message : 'Failed to create homeowner');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ResponsiveContainer className="container">
        <SharedHeaderAdmin onNavigate={onNavigate} />
        <div className="content">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: '#059669' }}>✅</div>
            <h2 style={{ fontSize: 24, fontWeight: '600', color: '#111827', marginBottom: 8 }}>
              Homeowner Created Successfully!
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              The homeowner account has been created with Clerk authentication and an initial project.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setSuccess(false)}
                className="btn btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Add Another Homeowner
              </button>
              <button
                onClick={() => onNavigate?.('admin-homeowners-list')}
                className="btn btn-primary"
                style={{ padding: '10px 20px' }}
              >
                Back to Homeowners List
              </button>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => onNavigate?.('admin-homeowners-list')}
              style={{ fontSize: 14, padding: '8px 16px' }}
            >
              ← Back to Homeowners
            </button>
            <h1 style={{ fontSize: 28, margin: 0 }}>Add New Homeowner</h1>
          </div>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Create a new homeowner account with Clerk authentication and initial project
          </p>
        </div>

        {error && (
          <div style={{ 
            background: '#fee2e2', 
            border: '1px solid #fecaca', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <SettingsCard title="Personal Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Address Information */}
          <SettingsCard title="Address Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Street Address *
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Project Information */}
          <SettingsCard title="Initial Project Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Project Type
                </label>
                <select
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                >
                  <option value="ROOF">Roof Replacement</option>
                  <option value="GUTTER">Gutter Installation</option>
                  <option value="SIDING">Siding</option>
                  <option value="WINDOWS">Windows</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Desired Start Date
                </label>
                <input
                  type="date"
                  value={desiredStart}
                  onChange={(e) => setDesiredStart(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Additional Project Information
                </label>
                <textarea
                  value={extraInfo}
                  onChange={(e) => setExtraInfo(e.target.value)}
                  className="form-control"
                  rows={3}
                  style={{ width: '100%' }}
                  placeholder="Any additional details about the project..."
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={roofScopeOnly}
                  onChange={(e) => setRoofScopeOnly(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>Roof scope only</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={isPartial}
                  onChange={(e) => setIsPartial(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>Partial project</span>
              </label>
            </div>
          </SettingsCard>

          {/* Clerk Account Information */}
          <SettingsCard title="Clerk Account Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                  minLength={8}
                />
                <small style={{ color: '#6b7280', fontSize: 12 }}>
                  Minimum 8 characters
                </small>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Preferences */}
          <SettingsCard title="Communication Preferences">
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={textOptIn}
                  onChange={(e) => setTextOptIn(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Opt-in to SMS/text notifications
                </span>
              </label>
            </div>
          </SettingsCard>

          {/* Submit Button */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ 
                fontSize: 16, 
                padding: '12px 32px',
                background: '#059669',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Creating Homeowner...' : 'Create Homeowner'}
            </button>
          </div>
        </form>
      </div>
    </ResponsiveContainer>
  );
};

export default AdminAddHomeowner;
// CB:END admin-add-homeowner
